import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { apiGet } from "../../api/client";
import type { FeedResponse } from "../../api/legacyTypes";
import { AppShell } from "../../app/AppShell";
import { TournamentCard } from "../../components/TournamentCard";
import { gameModule } from "../../games/registry";
import { sortTournamentsForCatalogue } from "../../utils/tournamentSort";

const statuses = ["all", "ongoing", "upcoming", "completed"] as const;

export function TournamentsPage() {
  const [params, setParams] = useSearchParams();
  const game = params.get("game") || "all";
  const status = params.get("status") || "all";
	const selectedGame = gameModule(game);
	const tierOneOnly = game === "chess" || game === "ageofempires" || game === "osu" || game === "apexlegends";
  const query = useQuery({
    queryKey: ["tournaments", game],
    queryFn: ({ signal }) => apiGet<FeedResponse>(`/api/v1/tournaments${game === "all" ? "" : `?game=${game}`}`, signal),
  });
  const tournaments = sortTournamentsForCatalogue(
    (query.data?.tournaments ?? []).filter((tournament) => status === "all" || tournament.catalogueStatus === status),
  );
  const changeGame = (nextGame: string) => {
    const next = new URLSearchParams(params);
    if (nextGame === "all") next.delete("game"); else next.set("game", nextGame);
    setParams(next);
  };
  const changeStatus = (nextStatus: string) => {
    const next = new URLSearchParams(params);
    if (nextStatus === "all") next.delete("status"); else next.set("status", nextStatus);
    setParams(next);
  };
  return <AppShell selectedGame={game} onGameChange={changeGame}>
	<section className="page-heading"><p className="eyebrow">Discover</p><h1>{game === "all" ? "All tournaments" : `${selectedGame?.name ?? "Game"} tournaments`}</h1><p>{game === "osu" ? "Tier 1 standard-mode osu! only — mania, taiko, catch, and other rulesets are excluded." : game === "ageofempires" ? "Tier 1 Age of Empires II tournaments only." : game === "chess" ? "Tier 1 tournaments, plus explicitly followed archive events." : game === "worldoftanks" || game === "rocketleague" ? "Tier 1–3 tournaments; Tier 1 events are followed automatically." : tierOneOnly ? "Tier 1 tournaments only." : "Browse every cached tournament and follow only the ones you want in the main feed."}</p></section>
    <div className="filter-row" role="group" aria-label="Tournament status">{statuses.map((item) => <button key={item} type="button" className={status === item ? "active" : ""} onClick={() => changeStatus(item)}>{item}</button>)}</div>
    {query.isPending && <p className="state-panel">Loading tournaments…</p>}
    {query.isError && <p className="state-panel state-error">Tournament discovery could not be loaded.</p>}
	{!query.isPending && !query.isError && tournaments.length === 0 && <p className="state-panel">{tierOneOnly ? `No current Tier 1 ${game === "osu" ? "standard-mode " : ""}tournaments match these filters.` : "No tournaments match these filters."}</p>}
    <div className="catalogue-grid">{tournaments.map((tournament) => <TournamentCard key={tournament.id} tournament={tournament} compact />)}</div>
  </AppShell>;
}
